import { type RefObject, useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import { getNodesBounds, useNodes, useReactFlow } from "@xyflow/react";

import { graphMinZoom } from "./ruleGraphViewport/graphMinZoom";
import { RULE_GRAPH_FIT_PADDING } from "./ruleGraphViewport/RULE_GRAPH_FIT_PADDING";
import { initialRuleGraphViewport } from "./ruleGraphViewport/initialRuleGraphViewport";

export function RuleGraphViewportSync({
  canvasRef,
  ready,
  revision,
  graphKey,
  minZoom,
  onMinZoom,
  positions,
  bounds,
  onApplied,
}: {
  canvasRef: RefObject<HTMLDivElement | null>;
  ready: boolean;
  revision: string;
  graphKey: string;
  minZoom: number;
  onMinZoom: (zoom: number) => void;
  positions: Map<string, { x: number; y: number }> | undefined;
  bounds: { x: number; y: number; width: number; height: number } | undefined;
  onApplied: (revision: string) => void;
}) {
  const { getViewport, setViewport } = useReactFlow();
  const nodes = useNodes();
  const [size, setSize] = useImmer({ width: 0, height: 0 });
  const fitted = useRef<string | null>(null);
  const visible = useRef(false);
  // The view last fitted and the canvas size it was fitted for. While the
  // author has not moved the view since, a resize — the rail opening or
  // closing, a wider window — fits the rule again, so it always opens framed
  // whole rather than at the zoom an earlier canvas size called for.
  const lastFit = useRef<{
    view: { x: number; y: number; zoom: number };
    size: { width: number; height: number };
  } | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current?.querySelector<HTMLElement>(".react-flow");
    if (!canvas) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(canvas);
    setSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    return () => observer.disconnect();
  }, [canvasRef, setSize]);
  useEffect(() => {
    if (size.width <= 0 || size.height <= 0) {
      visible.current = false;
      return;
    }
    if (!ready || !positions || !bounds || nodes.length !== positions.size) {
      return;
    }
    if (
      nodes.some((node) => {
        const point = positions.get(node.id);
        return (
          !point || point.x !== node.position.x || point.y !== node.position.y
        );
      })
    ) {
      return;
    }
    const next = graphMinZoom(bounds, size);
    onMinZoom(next);
    if (next !== minZoom) {
      return;
    }
    const resizedUntouched =
      lastFit.current !== null &&
      (lastFit.current.size.width !== size.width ||
        lastFit.current.size.height !== size.height) &&
      sameView(getViewport(), lastFit.current.view);
    if (!visible.current || fitted.current !== graphKey || resizedUntouched) {
      const view = initialRuleGraphViewport(bounds, size, next);
      void setViewport(view).then(() => onApplied(revision));
      lastFit.current = { view, size };
      fitted.current = graphKey;
      visible.current = true;
    } else {
      onApplied(revision);
    }
  }, [
    ready,
    revision,
    graphKey,
    minZoom,
    size,
    onMinZoom,
    getViewport,
    setViewport,
    nodes,
    positions,
    bounds,
    onApplied,
  ]);
  return null;
}

const sameView = (
  a: { x: number; y: number; zoom: number },
  b: { x: number; y: number; zoom: number }
) =>
  Math.abs(a.x - b.x) < 0.5 &&
  Math.abs(a.y - b.y) < 0.5 &&
  Math.abs(a.zoom - b.zoom) < 0.001;
